import { getFileExtension } from './fileTypeDetection';
import type { FileInput } from './types';

/**
 * Helper utilities for working with FileInput (File or ZipEntry)
 */

export function getSize(input: FileInput): number {
  return input instanceof File ? input.size : input.entry.uncompressedSize;
}

export function getType(input: FileInput): string {
  if (input instanceof File) return input.type;
  // Infer MIME type from extension for ZIP entries
  const ext = getFileExtension(input.name || input.entry.name);
  const mimeTypes: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    pdf: 'application/pdf',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  };
  return ext ? mimeTypes[ext] || '' : '';
}

export async function readBytes(input: FileInput, start: number, end: number): Promise<Uint8Array> {
  if (input instanceof File) {
    const buffer = await input.slice(start, end).arrayBuffer();
    return new Uint8Array(buffer);
  } else {
    return input.parser.readBytes(input.entry, start, end);
  }
}

export async function readTail(input: FileInput, byteCount: number): Promise<Uint8Array> {
  if (input instanceof File) {
    const buffer = await input.slice(-byteCount).arrayBuffer();
    return new Uint8Array(buffer);
  } else {
    return input.parser.readTail(input.entry, byteCount);
  }
}

export async function readMagicBytes(
  input: FileInput,
  byteCount: number = 16,
): Promise<Uint8Array> {
  if (input instanceof File) {
    const buffer = await input.slice(0, byteCount).arrayBuffer();
    return new Uint8Array(buffer);
  } else {
    return input.parser.getMagicBytes(input.entry, byteCount);
  }
}
